/**
 * Life Blueprint V2.9.9 — Migration Batch 06 (Sheet27: B36 / B37 / B38 / B66 / B67 / B68 / B69 / B70)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * STRICT SCOPE
 *   Implements ONLY these audited Sheet27 cells:
 *
 *     Year-pillar chain:   B36, B37, B38
 *     Four-pillar aggregation: B66, B67, B68, B69, B70
 *
 *   Nothing else. Not wired into the main V2.9.9 pipeline (still throws).
 *   Batch 01/02/03/04/05 files are intentionally left UNCHANGED. Where the
 *   Batch 06 chain needs B47 / B55 / B63, callers must produce them by
 *   running the already-validated Batch 03/04/05 slices — this module NEVER
 *   reimplements those cells.
 *
 * GOLDEN MASTER PROVENANCE (audited)
 *
 *   Sheet27!B36:
 *     Automatic branch (this sample) uses exact-match
 *     VLOOKUP(B34, Sheet40!$A:$D, 2, FALSE()).
 *     Audited B34=4 → Sheet40!A5:B5 = 丁 (U+4E01, code point 19969)
 *     → B36 = 丁.
 *
 *   Sheet27!B37:
 *     Automatic branch (this sample) uses exact-match
 *     VLOOKUP(B35, Sheet41!$A:$E, 2, FALSE()).
 *     Audited B35=6 → Sheet41!A7:B7 = 巳 (U+5DF3, code point 24051)
 *     → B37 = 巳.
 *
 *   Sheet27!B38 = B36 & B37    (deterministic Excel string concat)
 *     Audited B38 = 丁巳 (LEN=2, code points 19969 24051).
 *
 *   Sheet27!B66 = B38
 *   Sheet27!B67 = B47   (produced by Batch 03)
 *   Sheet27!B68 = B55   (produced by Batch 04)
 *   Sheet27!B69 = B63   (produced by Batch 05)
 *   Sheet27!B70 = B66 & " " & B67 & " " & B68 & " " & B69
 *     Audited B70 = "丁巳 壬寅 辛卯 丙申"
 *     (exactly one U+0020 ASCII space between each two-character component).
 *
 *   Excel equality checks previously executed and returned TRUE:
 *     B66 = B38, B67 = B47, B68 = B55, B69 = B63, and the full B70 concat.
 *
 * MANUAL-BRANCH POLICY FOR B36 / B37
 *   The B36/B37 formulas contain a manual override branch controlled by the
 *   same manual-mode condition audited in earlier batches (Batch 03
 *   `MANUAL_MODE_TOKEN` = '手动覆盖'). However, the manual-branch source
 *   cell for the year pillar has NOT been supplied as audited evidence in
 *   this batch. Inventing it (e.g. guessing I17/I18) is forbidden.
 *
 *   This port therefore:
 *     • Preserves the audited manual-mode CHECK (upstreamC32 ===
 *       MANUAL_MODE_TOKEN) using the Batch 03 audited token — no new token
 *       is invented.
 *     • Throws a labelled `UNAUDITED_MANUAL_BRANCH` error if manual mode
 *       is active, rather than fabricating a manual-branch calculation.
 *     • Executes the automatic exact-match VLOOKUP branch for the audited
 *       Golden Master (C32='自动排盘' → NOT manual → automatic branch).
 *
 * REFERENCE DATA
 *   Batch 06 requires two additional audited rows:
 *     Sheet40!A5:B5 (A=4 → B=丁, U+4E01)
 *     Sheet41!A7:B7 (A=6 → B=巳, U+5DF3)
 *   These are captured here as
 *   `BATCH06_PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE`, additively composed
 *   over the Batch 03 partial reference (which was itself already extended
 *   by Batch 05). Batch 03's file is NOT edited by this batch.
 *
 *   M02 MUST remain NOT_STARTED. Missing keys throw
 *   `MISSING_REFERENCE_EVIDENCE` — never a guessed value.
 *
 * DETERMINISM
 *   No randomness. No wall clock. Given identical input, byte-wise
 *   identical output on every run.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  MANUAL_MODE_TOKEN,
  PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE as BATCH03_PARTIAL_REFERENCE,
} from "@/engine/v299/batch03_sheet27";

/**
 * Batch 06 additive partial reference evidence.
 *
 * Composes over Batch 03's existing partial reference (which also holds the
 * additive rows previously added by Batch 05) and adds the two newly
 * audited rows required by B36 / B37. This is NOT a complete Sheet40 /
 * Sheet41 migration. M02 must remain NOT_STARTED.
 */
export const BATCH06_PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE = Object.freeze({
  Sheet40: Object.freeze({
    codeName: "Sheet40",
    worksheetIndex: 40,
    status: "PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE",
    note:
      "Only rows required by currently audited Golden Master samples have " +
      "been migrated. Batch 03 seeded A10=9→壬; Batch 05 added A4=3→丙; " +
      "Batch 06 now adds A5=4→丁. This is NOT a complete Sheet40 reference " +
      "table. M02 must remain NOT_STARTED.",
    rows: Object.freeze([
      ...BATCH03_PARTIAL_REFERENCE.Sheet40.rows,
      Object.freeze({
        a: 4,
        b: "\u4E01", // 丁
        unicodeCodePoint: 19969,
        sourceCell: "Sheet40!A5:B5",
        auditedInBatch: "Batch06",
      }),
    ]),
  }),
  Sheet41: Object.freeze({
    codeName: "Sheet41",
    worksheetIndex: 41,
    status: "PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE",
    note:
      "Only rows required by currently audited Golden Master samples have " +
      "been migrated. Batch 03 seeded A4=3→寅; Batch 05 added A10=9→申; " +
      "Batch 06 now adds A7=6→巳. This is NOT a complete Sheet41 reference " +
      "table. M02 must remain NOT_STARTED.",
    rows: Object.freeze([
      ...BATCH03_PARTIAL_REFERENCE.Sheet41.rows,
      Object.freeze({
        a: 6,
        b: "\u5DF3", // 巳
        unicodeCodePoint: 24051,
        sourceCell: "Sheet41!A7:B7",
        auditedInBatch: "Batch06",
      }),
    ]),
  }),
});

/**
 * Excel-compatible exact-match VLOOKUP over a partial reference table.
 * Missing keys throw `MISSING_REFERENCE_EVIDENCE` — never a guessed value.
 * Matches the Batch 03 / Batch 05 contract (the audited B36/B37 Excel
 * formulas are NOT wrapped in IFERROR — do not adopt Batch 04's "" fallback).
 */
function vlookupExactMatch(key, refTable) {
  if (!refTable || !Array.isArray(refTable.rows)) {
    throw new TypeError("[Batch06·VLOOKUP] reference table missing or malformed");
  }
  const row = refTable.rows.find((r) => r.a === key);
  if (!row) {
    const err = new Error(
      `[Batch06·VLOOKUP] Missing ${refTable.codeName} key ${JSON.stringify(key)}. ` +
        `Only PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE has been migrated for ` +
        `this reference table; the required row has not yet been audited. ` +
        `No result guessed.`
    );
    err.code = "MISSING_REFERENCE_EVIDENCE";
    err.refTable = refTable.codeName;
    err.key = key;
    throw err;
  }
  return row.b;
}

function throwUnauditedManualBranch(cell) {
  const err = new Error(
    `[Batch06·${cell}] Manual override branch requested (upstreamC32 === ` +
      "MANUAL_MODE_TOKEN) but the year-pillar manual-branch source cell " +
      "has NOT been independently audited in this batch. Refusing to guess " +
      "an unaudited I-column reference."
  );
  err.code = "UNAUDITED_MANUAL_BRANCH";
  err.cell = cell;
  throw err;
}

/**
 * Sheet27!B36.
 * Automatic branch: VLOOKUP(B34, Sheet40!$A:$D, 2, FALSE).
 * Manual branch throws `UNAUDITED_MANUAL_BRANCH` — see file header for policy.
 *
 * @param {{
 *   upstreamC32?: string,
 *   upstreamValue_B34: number,
 *   reference?: typeof BATCH06_PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE
 * }} slice
 * @returns {string}
 */
export function computeB36(slice) {
  if (slice === null || typeof slice !== "object") {
    throw new TypeError("[Batch06·B36] slice must be an object");
  }
  const ref = slice.reference || BATCH06_PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE;
  if (slice.upstreamC32 === MANUAL_MODE_TOKEN) {
    throwUnauditedManualBranch("B36");
  }
  if (!Number.isInteger(slice.upstreamValue_B34)) {
    throw new TypeError(
      "[Batch06·B36] upstreamValue_B34 must be an integer in the automatic branch"
    );
  }
  return vlookupExactMatch(slice.upstreamValue_B34, ref.Sheet40);
}

/**
 * Sheet27!B37.
 * Automatic branch: VLOOKUP(B35, Sheet41!$A:$E, 2, FALSE).
 * Manual branch throws `UNAUDITED_MANUAL_BRANCH`.
 *
 * @param {{
 *   upstreamC32?: string,
 *   upstreamValue_B35: number,
 *   reference?: typeof BATCH06_PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE
 * }} slice
 * @returns {string}
 */
export function computeB37(slice) {
  if (slice === null || typeof slice !== "object") {
    throw new TypeError("[Batch06·B37] slice must be an object");
  }
  const ref = slice.reference || BATCH06_PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE;
  if (slice.upstreamC32 === MANUAL_MODE_TOKEN) {
    throwUnauditedManualBranch("B37");
  }
  if (!Number.isInteger(slice.upstreamValue_B35)) {
    throw new TypeError(
      "[Batch06·B37] upstreamValue_B35 must be an integer in the automatic branch"
    );
  }
  return vlookupExactMatch(slice.upstreamValue_B35, ref.Sheet41);
}

/**
 * Sheet27!B38 = B36 & B37   (deterministic Excel string concatenation).
 */
export function computeB38(B36, B37) {
  return String(B36) + String(B37);
}

/**
 * Sheet27!B66 = B38 — direct assignment, preserved as a passthrough helper
 * so provenance labelling ("DIRECTLY_REPRODUCED_FROM_B38") is explicit at
 * the call site.
 */
export function computeB66(upstreamValue_B38) {
  if (typeof upstreamValue_B38 !== "string") {
    throw new TypeError("[Batch06·B66] upstreamValue_B38 must be a string");
  }
  return upstreamValue_B38;
}

/**
 * Sheet27!B67 = B47. B47 is produced by the already-validated Batch 03
 * slice — this module does NOT reimplement it. Caller supplies the value.
 */
export function computeB67(upstreamValue_B47) {
  if (typeof upstreamValue_B47 !== "string") {
    throw new TypeError("[Batch06·B67] upstreamValue_B47 must be a string");
  }
  return upstreamValue_B47;
}

/**
 * Sheet27!B68 = B55. B55 is produced by the already-validated Batch 04
 * slice — this module does NOT reimplement it. Caller supplies the value.
 */
export function computeB68(upstreamValue_B55) {
  if (typeof upstreamValue_B55 !== "string") {
    throw new TypeError("[Batch06·B68] upstreamValue_B55 must be a string");
  }
  return upstreamValue_B55;
}

/**
 * Sheet27!B69 = B63. B63 is produced by the already-validated Batch 05
 * slice — this module does NOT reimplement it. Caller supplies the value.
 */
export function computeB69(upstreamValue_B63) {
  if (typeof upstreamValue_B63 !== "string") {
    throw new TypeError("[Batch06·B69] upstreamValue_B63 must be a string");
  }
  return upstreamValue_B63;
}

/**
 * Sheet27!B70 = B66 & " " & B67 & " " & B68 & " " & B69.
 * Exactly one ASCII space (U+0020) between each of the four two-character
 * components — preserving the audited Excel concatenation byte-for-byte.
 */
export function computeB70(B66, B67, B68, B69) {
  return (
    String(B66) + " " + String(B67) + " " + String(B68) + " " + String(B69)
  );
}

/**
 * Aggregate entry point for Batch 06.
 *
 * B36 / B37 / B38 are computed from B34 / B35 via Sheet40 / Sheet41.
 * B66 / B67 / B68 / B69 / B70 require the caller to supply the upstream
 * B47 / B55 / B63 values (produced by Batches 03 / 04 / 05 — this module
 * NEVER reimplements those cells).
 *
 * @param {{
 *   upstreamC32?: string,
 *   upstreamValue_B34: number,
 *   upstreamValue_B35: number,
 *   upstreamValue_B47: string,
 *   upstreamValue_B55: string,
 *   upstreamValue_B63: string,
 *   reference?: typeof BATCH06_PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE
 * }} slice
 * @returns {{
 *   B36:string, B37:string, B38:string,
 *   B66:string, B67:string, B68:string, B69:string, B70:string,
 *   _provenance:Object
 * }}
 */
export function computeBatch06Sheet27(slice) {
  if (slice === null || typeof slice !== "object") {
    throw new TypeError("[Batch06] slice must be an object");
  }
  const reference = slice.reference || BATCH06_PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE;
  const sliceWithRef = { ...slice, reference };
  const B36 = computeB36(sliceWithRef);
  const B37 = computeB37(sliceWithRef);
  const B38 = computeB38(B36, B37);
  const B66 = computeB66(B38);
  const B67 = computeB67(slice.upstreamValue_B47);
  const B68 = computeB68(slice.upstreamValue_B55);
  const B69 = computeB69(slice.upstreamValue_B63);
  const B70 = computeB70(B66, B67, B68, B69);
  return {
    B36,
    B37,
    B38,
    B66,
    B67,
    B68,
    B69,
    B70,
    _provenance: {
      excelSources: [
        "Sheet27!B36",
        "Sheet27!B37",
        "Sheet27!B38",
        "Sheet27!B66",
        "Sheet27!B67",
        "Sheet27!B68",
        "Sheet27!B69",
        "Sheet27!B70",
      ],
      formulas: {
        "Sheet27!B36":
          "=IF('01_个人资料'!$C$32=<manual-mode-token>," +
          "<UNAUDITED_MANUAL_BRANCH>,VLOOKUP(B34,Sheet40!$A:$D,2,FALSE))",
        "Sheet27!B37":
          "=IF('01_个人资料'!$C$32=<manual-mode-token>," +
          "<UNAUDITED_MANUAL_BRANCH>,VLOOKUP(B35,Sheet41!$A:$E,2,FALSE))",
        "Sheet27!B38": "=B36&B37",
        "Sheet27!B66": "=B38",
        "Sheet27!B67": "=B47",
        "Sheet27!B68": "=B55",
        "Sheet27!B69": "=B63",
        "Sheet27!B70": '=B66&" "&B67&" "&B68&" "&B69',
      },
      lookupSources: {
        B36: { worksheetIndex: 40, codeName: "Sheet40" },
        B37: { worksheetIndex: 41, codeName: "Sheet41" },
      },
      auditedRecords: {
        "Sheet40!A5:B5": {
          key: 4,
          value: "\u4E01",
          unicodeCodePoint: 19969,
          auditedInBatch: "Batch06",
        },
        "Sheet41!A7:B7": {
          key: 6,
          value: "\u5DF3",
          unicodeCodePoint: 24051,
          auditedInBatch: "Batch06",
        },
      },
      partialReferenceEvidence: {
        Sheet40: BATCH06_PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE.Sheet40.status,
        Sheet41: BATCH06_PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE.Sheet41.status,
      },
      manualModeToken: MANUAL_MODE_TOKEN, // reused from Batch 03 evidence closure
      note:
        "Business meaning of B34/B35/B36/B37/B38/B66/B67/B68/B69/B70 and of " +
        "Sheet40/Sheet41 lookup values is intentionally NOT labelled — no " +
        "independent evidence has established their semantics. Manual " +
        "override branch for the year pillar is intentionally guarded by " +
        "UNAUDITED_MANUAL_BRANCH rather than fabricated.",
    },
  };
}
